import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { AppointmentService } from 'services/appointments';
import { useUserStore } from 'stores/user';
import { type CancelAppointmentPayload } from 'types/appointments';

export const useCancelAppointment = (
  appointmentId: string
): UseMutationResult<void, Error, CancelAppointmentPayload> => {
  const queryClient = useQueryClient();
  const isPsychologist = useUserStore(state => state.isPsychologist);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  return useMutation<void, Error, CancelAppointmentPayload>({
    mutationKey: [MUTATION_KEYS.CANCEL_APPOINTMENT, appointmentId],
    mutationFn: async (body: CancelAppointmentPayload) => {
      if (isPsychologist) {
        await AppointmentService.cancelFromPsychologist(body);
        return;
      }
      await AppointmentService.cancel(body);
    },
    onSuccess: () => {
      toast.success(t('CancelAppointment.success'));
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_NEXT_APPOINTMENTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_COUPONS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_PATIENT] });
      if (isPsychologist) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_PATIENT_APPOINTMENTS] });
      }
    },
    onError: () => {
      toast.error(t('CancelAppointment.error'));
    },
  });
};
